import React, { useState } from 'react'
import Button from 'components/shared/Button'
import { FormControlLabel, Checkbox, Tooltip, Chip, MenuItem, Select } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import styles from 'components/forms/Checkbox/Checkbox.module.scss'
import strings from 'strings'

const DEMO_ORG_SUGGESTIONS = ['pharmacy', 'medical group', 'health']
const DEMO_INDIVIDUAL_SUGGESTIONS = ['kelley', 'smith', 'alvarez']

const fieldWrap = { display: 'flex', flexDirection: 'column', flex: '1 1 200px', minWidth: 160 }
const fieldRow = { display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }
const inputStyle = { padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }
const labelStyle = { fontSize: 12, marginBottom: 4, color: '#555', display: 'flex', alignItems: 'center', gap: 4 }

const TOOLTIPS = {
    npi: 'National Provider Identifier — 10-digit unique ID for US healthcare providers. Assigned by CMS via the NPPES registry.',
    entityType: 'NPI-1 = individual clinician (physician, nurse, etc.); NPI-2 = organization (hospital, group practice, pharmacy).',
    taxonomyCode: 'Healthcare Provider Taxonomy Code — 10-character code identifying provider specialty. See NUCC.org.',
    credential: 'Credentials the provider holds (MD, DO, RN, LCSW, etc.). Partial match supported.',
}

const NppesSearchFields = ({ loading, handleSearch, isQuickSearch, isDemoMode, prefillTargets }) => {
    // In an application tab, prefill orgName from the business by default; a chip row lets the
    // user swap to search for an individual owner instead. Clearing the opposite fields matters
    // more here than in SAM.gov — NPPES buildSpec AND's all predicates, so an org+person combo
    // would return zero rows (no row is both an org and a person).
    const businessPrefill = prefillTargets?.business || ''
    const [npi, setNpi] = useState('')
    const [entityType, setEntityType] = useState(() => businessPrefill ? 'NPI-2' : '')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [orgName, setOrgName] = useState(() => businessPrefill)
    const [selectedPrefill, setSelectedPrefill] = useState(() => businessPrefill ? 'business' : null)
    const [state, setState] = useState('')
    const [credential, setCredential] = useState('')
    const [taxonomyCode, setTaxonomyCode] = useState('')
    const [taxonomyDesc, setTaxonomyDesc] = useState('')
    const [activeOnly, setActiveOnly] = useState(true)

    const canSubmit = npi || firstName || lastName || orgName || taxonomyCode || taxonomyDesc || credential

    const onSubmit = () => {
        if (loading) return
        handleSearch({
            npi: npi || null,
            entityType: entityType || null,
            firstName: firstName || null,
            lastName: lastName || null,
            orgName: orgName || null,
            state: state || null,
            credential: credential || null,
            taxonomyCode: taxonomyCode || null,
            taxonomyDesc: taxonomyDesc || null,
            activeOnly
        })
    }

    const labelWithTooltip = (text, tooltipText) => (
        <label style={labelStyle}>
            {text}
            {tooltipText && (
                <Tooltip title={tooltipText} arrow placement='top'>
                    <InfoOutlinedIcon style={{ fontSize: 14, color: '#888', cursor: 'help' }} />
                </Tooltip>
            )}
        </label>
    )

    const field = (labelEl, value, setter, id, placeholder = '') => (
        <div style={fieldWrap}>
            {labelEl}
            <input
                style={inputStyle}
                value={value}
                onChange={(e) => setter(e.target.value)}
                placeholder={placeholder}
                id={id}
            />
        </div>
    )

    const applyOrgSuggestion = (term) => {
        setOrgName(term)
        setEntityType('NPI-2')
        setFirstName('')
        setLastName('')
        setNpi('')
    }
    const applyIndividualSuggestion = (term) => {
        setLastName(term)
        setEntityType('NPI-1')
        setOrgName('')
        setNpi('')
    }

    // Prefill picker — swap the search target between the application's business (NPI-2 search)
    // and any of its owners (NPI-1 search). Clears the opposite fields each time.
    const selectBusiness = () => {
        setOrgName(businessPrefill)
        setEntityType('NPI-2')
        setFirstName('')
        setLastName('')
        setSelectedPrefill('business')
    }
    const selectOwner = (owner, index) => {
        setOrgName('')
        setEntityType('NPI-1')
        setFirstName(owner.firstName || '')
        setLastName(owner.lastName || '')
        setSelectedPrefill(`owner-${index}`)
    }
    const owners = prefillTargets?.owners || []
    const showPrefillPicker = businessPrefill || owners.length > 0
    const prefillChipStyle = (isSelected) => ({
        background: isSelected ? 'var(--rox-lilac-ink)' : 'var(--badge-review-bg)',
        color: isSelected ? '#fff' : 'var(--rox-lilac-ink)',
        fontWeight: isSelected ? 600 : 400,
    })

    return (
        <div>
            {showPrefillPicker && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-canvas)', borderRadius: 4, fontSize: 12 }}>
                    <div style={{ color: '#555', marginBottom: 6 }}>
                        <strong>Prefill from application</strong> — click to switch search target:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {businessPrefill && (
                            <Chip
                                label={`Business: ${businessPrefill}`}
                                size='small'
                                clickable
                                onClick={selectBusiness}
                                style={prefillChipStyle(selectedPrefill === 'business')}
                            />
                        )}
                        {owners.map((owner, i) => (
                            <Chip
                                key={i}
                                label={`Owner: ${owner.label}`}
                                size='small'
                                clickable
                                onClick={() => selectOwner(owner, i)}
                                style={prefillChipStyle(selectedPrefill === `owner-${i}`)}
                            />
                        ))}
                    </div>
                </div>
            )}
            {isDemoMode && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontSize: 12 }}>
                    <div style={{ color: '#555', marginBottom: 6 }}>
                        <strong>Demo mode suggestions</strong> — click to fill:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ color: '#666', minWidth: 90 }}>Organizations →</span>
                        {DEMO_ORG_SUGGESTIONS.map(term => (
                            <Chip
                                key={term}
                                label={term}
                                size='small'
                                clickable
                                onClick={() => applyOrgSuggestion(term)}
                                style={{ background: '#fff' }}
                            />
                        ))}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: '#666', minWidth: 90 }}>Individuals →</span>
                        {DEMO_INDIVIDUAL_SUGGESTIONS.map(term => (
                            <Chip
                                key={term}
                                label={term}
                                size='small'
                                clickable
                                onClick={() => applyIndividualSuggestion(term)}
                                style={{ background: '#fff' }}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div style={fieldRow}>
                {field(labelWithTooltip('NPI', TOOLTIPS.npi), npi, setNpi, 'nppesNpi', '10-digit NPI')}
                <div style={fieldWrap}>
                    {labelWithTooltip('Entity Type', TOOLTIPS.entityType)}
                    <Select
                        value={entityType}
                        onChange={(e) => setEntityType(e.target.value)}
                        displayEmpty
                        style={{ fontSize: 14, padding: 0 }}
                    >
                        <MenuItem value=''>Any</MenuItem>
                        <MenuItem value='NPI-1'>NPI-1 (Individual)</MenuItem>
                        <MenuItem value='NPI-2'>NPI-2 (Organization)</MenuItem>
                    </Select>
                </div>
                {field(labelWithTooltip('State'), state, setState, 'nppesState', 'e.g. CA')}
            </div>
            <div style={fieldRow}>
                {field(labelWithTooltip('First Name'), firstName, setFirstName, 'nppesFirstName')}
                {field(labelWithTooltip('Last Name'), lastName, setLastName, 'nppesLastName')}
                {field(labelWithTooltip('Organization Name'), orgName, setOrgName, 'nppesOrgName', 'e.g. Kaiser')}
            </div>
            <div style={fieldRow}>
                {field(labelWithTooltip('Credential', TOOLTIPS.credential), credential, setCredential, 'nppesCredential', 'e.g. MD, RN')}
                {field(labelWithTooltip('Taxonomy Code', TOOLTIPS.taxonomyCode), taxonomyCode, setTaxonomyCode, 'nppesTaxCode')}
                {field(labelWithTooltip('Specialty'), taxonomyDesc, setTaxonomyDesc, 'nppesTaxDesc', 'e.g. Internal Medicine')}
            </div>
            <div className='d-flex align-items-center justify-content-between mt-2' style={{ gap: '16px' }}>
                <FormControlLabel
                    className={styles.centeredCheckbox}
                    control={
                        <Checkbox
                            id='nppesActiveOnly'
                            checked={activeOnly}
                            onChange={(e) => setActiveOnly(e.target.checked)}
                            color='primary'
                        />
                    }
                    label='Active providers only'
                />
                <Button
                    onClick={onSubmit}
                    loading={loading}
                    disabled={!canSubmit}
                    id={`perform-nppes-search-${isQuickSearch ? 'qs' : 'app'}`}
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                    {strings.SEARCH}
                </Button>
            </div>
        </div>
    )
}

export default NppesSearchFields
